import { canv } from "@/canv/global";
import { frameWidget } from "@/elements/frame";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import type { PresentationSlide } from "@/elements/Presentation/types";
import { Group } from "@/group";

/**
 * Централизованный менеджер для управления overlapped группами всех фреймов.
 * Гарантирует, что каждая группа может быть захвачена только одним фреймом.
 * Также отслеживает удаляемые группы для предотвращения повторного удаления.
 */
class FrameOverlappedManager {
    // Map: frameUID -> массив захваченных групп
    private frameToGroups: Map<string, Group[]> = new Map();

    // Set для быстрой проверки уникальности
    private capturedUIDs: Set<string> = new Set();

    // Set для отслеживания групп, помеченных на удаление
    private deletionUIDs: Set<string> = new Set();

    /**
     * Очищает данные об overlapped группах
     */
    clearOverlapped() {
        this.frameToGroups.clear();
        this.capturedUIDs.clear();
    }

    /**
     * Помечает группу как удаляемую
     * @param groupUID - UID группы
     * @returns true если группа помечена впервые, false если уже была помечена
     */
    markForDeletion(groupUID: string): boolean {
        if (this.deletionUIDs.has(groupUID)) {
            return false;
        }
        this.deletionUIDs.add(groupUID);
        return true;
    }

    /**
     * Пытается добавить группу к фрейму
     * @param frameUID - UID фрейма
     * @param group - группа для добавления
     * @returns true если группа добавлена, false если уже захвачена другим фреймом
     */
    addGroup(frameUID: string, group: Group): boolean {
        // Если группа уже захвачена, не добавляем
        if (this.capturedUIDs.has(group.UID)) {
            return false;
        }

        // Создаем массив для фрейма, если его еще нет
        if (!this.frameToGroups.has(frameUID)) {
            this.frameToGroups.set(frameUID, []);
        }

        // Добавляем группу
        this.frameToGroups.get(frameUID)!.push(group);
        this.capturedUIDs.add(group.UID);
        return true;
    }

    /**
     * Получает все группы, захваченные конкретным фреймом
     * @param frameUID - UID фрейма
     * @returns массив групп или пустой массив
     */
    getGroups(frameUID: string): Group[] {
        return this.frameToGroups.get(frameUID) || [];
    }

    /**
     * Собирает все группы для удаления, включая overlapped группы фреймов
     * @param groups - исходные группы для удаления
     * @returns массив всех групп с учетом overlapped
     */
    collectGroupsForDeletion(groups: Group[]): Group[] {
        const result: Group[] = [];
        this.deletionUIDs.clear();

        for (const group of groups) {
            if (!this.markForDeletion(group.UID)) continue;

            result.push(group);

            for (const child of group.children) {
                // презентация и все фреймы ее и весь контент на фреймах тоже должны удалиться
                if (child instanceof PresentationWidget) {
                    child.slides.forEach((slide: PresentationSlide) => {
                        const frameGroup = canv.canvasController.FindByUID(slide.frameUID);

                        if (frameGroup && frameGroup instanceof Group) {
                            if (this.markForDeletion(frameGroup.UID)) {
                                result.push(frameGroup);
                            }

                            const frameChild = frameGroup.children[0];
                            if (frameChild instanceof frameWidget) {
                                const overlappedGroups = frameChild.getOverlappedGroups();

                                for (const overlapped of overlappedGroups) {
                                    if (this.markForDeletion(overlapped.UID)) {
                                        result.push(overlapped);
                                    }
                                }
                            }
                        }
                    });
                }

                // весь контент на фрейме тоже должен быть удален
                if (child instanceof frameWidget) {
                    const overlappedGroups = child.getOverlappedGroups();

                    for (const overlapped of overlappedGroups) {
                        if (this.markForDeletion(overlapped.UID)) {
                            result.push(overlapped);
                        }
                    }
                }
            }
        }

        this.deletionUIDs.clear();
        return result;
    }
}

export const frameOverlappedManager = new FrameOverlappedManager();
